<template>
  <div v-if="isVisible" class="dt-email-submit" :id="id">
    <!-- E-Mail-Eingabefeld -->
    <DTInput
      ref="emailInput"
      type="text"
      :id="inputId"
      :payload="payload"
      :placeholder="placeholderText"
      :required="required"
      @input="validateEmail"
    />
    
    <!-- Fehlermeldung -->
    <div v-if="isError" class="error-message">
      {{ errorMessage }}
    </div>
    
    <!-- Submit-Button -->
    <DTButton
      :text="getButtonText"
      :icon="buttonIcon"
      :iconPosition="buttonIconPosition"
      :externalLoading="loading"
      :disabled="loading || isError || !email || showSuccessMessage"
      :type="buttonType"
      @click="submitData"
    />
  </div>
</template>

<script>
import DTInput from "./DTInput.vue";
import DTButton from "./DTButton.vue";
import axios from "axios";
import { ref, onMounted, inject, nextTick, computed, getCurrentInstance } from "vue";
import { trackEvent } from "../utils/tracking";
import { safeGetItem } from "../utils/safeStorage";
import { builderText, customizerText } from "../utils/customizerTexts";

export default {
  name: "DTEmailSubmit",
  components: {
    DTInput,
    DTButton
  },
  props: {
    id: {
      type: String,
      default: "",
    },
    inputId: {
      type: String,
      default: "",
    },
    payload: {
      type: String,
      default: "email",
    },
    placeholder: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: true,
    },
    buttonText: {
      type: String,
      default: "",
    },
    buttonIcon: {
      type: String,
      default: "",
    },
    buttonIconPosition: {
      type: String,
      default: "right",
    },
    buttonType: {
      type: String,
      default: "secondary",
      validator: function (value) {
        return ["primary", "secondary", "tertiary"].includes(value);
      },
    },
    webhookUrl: {
      type: String,
      required: true,
    },
    successMessage: {
      type: String,
      default: "",
    },
    errorMessageInvalid: {
      type: String,
      default: "",
    },
    errorMessageSubmit: {
      type: String,
      default: "",
    },
    autoHideTimeout: {
      type: Number,
      default: 0,
      description: "Zeit in Sekunden, nach der die Komponente automatisch ausgeblendet wird. 0 = nie ausblenden."
    },
  },
  setup(props) {
    const email = ref("");
    const isError = ref(false);
    const errorMessage = ref("");
    const loading = ref(false);
    const showSuccessMessage = ref(false);
    const emailInput = ref(null);
    const isVisible = ref(true);
    
    const instance = getCurrentInstance();
    
    const pageSettings = inject("pageSettings", {});
    
    function validateEmail(event) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      email.value = event?.target?.value || emailInput.value?.value;
      isError.value = email.value && !emailRegex.test(email.value);
      errorMessage.value = isError.value ? builderText(props.errorMessageInvalid, "emailInvalid") : "";
    }
    
    function submitData() {
      if (isError.value || !email.value) return;
      
      loading.value = true;
      showSuccessMessage.value = false;
      
      trackEvent(instance.proxy, {
        event: "email_submit",
        category: "Customizer",
        action: "EmailSubmit",
        label: props.placeholder,
        input_id: props.inputId,
        email_domain: email.value.split('@')[1] || 'unknown'
      });
      
      const step1Values = safeGetItem(pageSettings.step1StorageKey, "{}") || "{}";
      const step2Values = safeGetItem(pageSettings.step2StorageKey, "{}") || "{}";
      const step3Values = safeGetItem(pageSettings.step3StorageKey, "{}") || "{}";
      const variantId = pageSettings.variant || "";
      
      if (process.env.VUE_APP_DEV_MODE === "true") {
        setTimeout(() => {
          showSuccessMessage.value = true;
          if (emailInput.value && emailInput.value.resetValue) {
            emailInput.value.resetValue();
          }
          email.value = "";
          loading.value = false;
        }, 1000);
      } else {
        axios.post(
          props.webhookUrl,
          {
            email: email.value,
            step1Values: step1Values,
            step2Values: step2Values,
            step3Values: step3Values,
            variantID: variantId,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            customizerFeature: "email_submit",
          }
        )
        .then(() => {
          showSuccessMessage.value = true;
          if (emailInput.value && emailInput.value.resetValue) {
            emailInput.value.resetValue();
          }
          email.value = "";
        })
        .catch(error => {
          // sentry-ignore: the failed request is reported by installAxiosReporting (feature email_submit)
          if (process.env.VUE_APP_DEV_MODE === "true") {
            console.error("Submit Error", {
              code: error?.code || "unknown",
              status: error?.response?.status || "none",
            });
          }
          isError.value = true;
          errorMessage.value = builderText(props.errorMessageSubmit, "submitError");
        })
        .finally(() => {
          loading.value = false;
        });
      }
    }
    
    function resetForm() {
      if (emailInput.value && emailInput.value.resetValue) {
        emailInput.value.resetValue();
      }
      email.value = "";
      isError.value = false;
      errorMessage.value = "";
      showSuccessMessage.value = false;
    }
    
    onMounted(() => {
      nextTick(() => {
        if (emailInput.value) {
          email.value = emailInput.value.value;
        }
      });
      
      setTimeout(() => {
        if (emailInput.value && emailInput.value.value) {
          validateEmail({ target: { value: emailInput.value.value } });
        }
      }, 300);

      if (props.autoHideTimeout > 0) {
        setTimeout(() => {
          isVisible.value = false;
        }, props.autoHideTimeout * 1000);
      }
    });
    
    const getButtonText = computed(() => {
      if (loading.value) {
        return customizerText("sending");
      } else if (showSuccessMessage.value) {
        return builderText(props.successMessage, "sent");
      } else {
        return builderText(props.buttonText, "submit");
      }
    });

    const placeholderText = computed(() => builderText(props.placeholder, "emailPlaceholder"));
    
    return {
      email,
      isError,
      errorMessage,
      loading,
      showSuccessMessage,
      emailInput,
      validateEmail,
      submitData,
      resetForm,
      getButtonText,
      placeholderText,
      isVisible
    };
  }
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;

.dt-email-submit {
  display: flex;
  flex-direction: column;
  width: 100%;
  
  .error-message {
    margin-top: 8px;
    padding-left: 32px;
    color: $color-input-error;
    font-size: 14px;
    
    @media (max-width: $breakpoint-tablet) {
      padding-left: 24px;
    }
  }
  
  .dt-button {
    margin-top: 16px;
    align-self: center;
    
    @media (max-width: $breakpoint-mobile) {
      width: 100%;
    }
  }
  
  .message-box {
    margin-top: 24px;
  }
}
</style>
